import React, { useState, useMemo } from 'react';
import Header from '../components/header';
import { statusText, complianceText, faultCodeConfig } from '../utils/texts';
import useMQTT from '../hooks/useMQTT';
import PlayIcon from '../icons/play';
import PauseIcon from '../icons/pause';
import { createCSV } from '../utils/csv';
import { StatusTable } from '../components/status-table';
import { FilterGroup } from '../components/filter/filter-group';

const COMPLIANCE_ITEMS: Status.ComplianceStatus[] = ['OK', 'FAIL', 'NOT_IN_SERVICE', 'UNKNOWN'];
const JOURNEY_STATUS_ITEMS: Status.JourneyStatus[] = ['ON_SERVICE_JOURNEY', 'UNKNOWN', 'ON_DEAD_RUN'];

export default function StatusContainer() {
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const { messages, connected, paused, setPaused } = useMQTT();

  function setFilterType(type: string, value: string) {
    return () => {
      if (filters[type] && filters[type].includes(value)) {
        setFilters({
          ...filters,
          [type]: filters[type].filter((f) => f !== value),
        });
      } else {
        setFilters({
          ...filters,
          [type]: [...(filters[type] || []), value],
        });
      }
    };
  }

  const operators = useMemo(() => {
    return Object.keys(messages)
      .map((top) => top.split('/')[0])
      .filter((value: string, index: number, self: string[]) => self.indexOf(value) === index);
  }, [messages]);

  const filteredItems = useMemo(() => {
    return Object.entries(messages).filter(filterItems);
  }, [messages, filters]);

  function filterItems([topic, item]: [string, Status.Item]) {
    return !Object.entries(filters).some(([key, values]) => {
      // No filter set
      if (values.length === 0) {
        return false;
      }
      // Operator is part of topic
      if (key === 'operators') {
        return !values.includes(topic.split('/')[0]);
      }
      // Only array filter
      if (key === 'complianceFaults') {
        return !values.some((val) => item[key].includes(val as unknown as Status.EComplianceFaults));
      }
      // Anything else is string
      return !values.includes(item[key]);
    });
  }

  function downloadState() {
    const date = new Date();
    const dateStr = date.toUTCString();
    const text = createCSV(filteredItems);
    const aEl = document.createElement('a');
    aEl.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(text));
    aEl.setAttribute('download', `status-${dateStr}.txt`);
    aEl.click();
  }

  const filtersCount = useMemo(() => {
    return Object.entries(filters).reduce((sum, [key, items]) => {
      return sum + items.length;
    }, 0);
  }, [filters]);

  return (
    <>
      <Header />
      <div className="flex-container">
        <div className="flex-side">
          <div className="d-flex flex-row justify-content-between align-items-center">
            <h2>Filter</h2>
            <span className="badge rounded-pill text-bg-primary">{filtersCount}</span>
          </div>
          {operators.length > 1 && (
            <FilterGroup
              label="Operator"
              groupName="operators"
              selected={filters.operators || []}
              onChange={setFilterType}
              items={operators.map((op) => [op, op])}
            />
          )}
          <FilterGroup
            label="Compliance"
            groupName="compliance"
            selected={filters.compliance || []}
            onChange={setFilterType}
            items={COMPLIANCE_ITEMS.map((key) => [key, complianceText[key]])}
          />
          <FilterGroup
            label="Journey Status"
            groupName="journeyStatus"
            selected={filters.journeyStatus || []}
            onChange={setFilterType}
            items={JOURNEY_STATUS_ITEMS.map((key) => [key, statusText[key]])}
          />
          <FilterGroup
            label="Compliance Fault"
            groupName="complianceFaults"
            selected={filters.complianceFaults || []}
            onChange={setFilterType}
            items={Object.entries(faultCodeConfig).map(([key, value]) => [key, value.short])}
          />
        </div>
        <div className="flex-main">
          <div>
            {connected ? 'Connected' : 'Not Connected'} - Showing {filteredItems.length} of{' '}
            {Object.keys(messages).length} vehicles
            <button className="btn btn-light" onClick={() => setPaused(!paused)}>
              {paused ? <PlayIcon className="icon" /> : <PauseIcon className="icon" />}
              {paused ? 'Continue' : 'Pause'}
            </button>
            <button className="btn btn-light" onClick={downloadState}>
              Download
            </button>
          </div>
          <StatusTable data={filteredItems} />
        </div>
      </div>
    </>
  );
}
